import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { getI18n } from '@/lib/i18n';

const ChatContext = createContext(null);

const truncate = (s, n = 50) => {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n)}…` : s;
};

export function ChatProvider({ children }) {
  const { user } = useAuth();
  const { t } = getI18n(user);
  const [open, setOpen] = useState(false);
  const [activeKey, setActiveKey] = useState(null); // `${tender_id}:${supplier_email}`
  const [threads, setThreads] = useState([]);
  const [unread, setUnread] = useState(0);
  // Шина live-событий чата для прямой подписки виджета (без re-fetch).
  const subsRef = useRef(new Set());
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const pingRef = useRef(null);

  const subscribe = useCallback((fn) => {
    subsRef.current.add(fn);
    return () => { subsRef.current.delete(fn); };
  }, []);

  const emit = useCallback((evt) => {
    subsRef.current.forEach((fn) => {
      try { fn(evt); } catch {}
    });
  }, []);

  const refreshThreads = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/chat/threads');
      setThreads(data || []);
      setUnread((data || []).reduce((s, t) => s + (t.unread_count || 0), 0));
    } catch {}
  }, [user]);

  const openThread = useCallback((tenderId, supplierEmail) => {
    setActiveKey(`${tenderId}:${(supplierEmail || '').toLowerCase()}`);
    setOpen(true);
  }, []);

  const closeWidget = useCallback(() => setOpen(false), []);
  const toggleWidget = useCallback(() => setOpen((v) => !v), []);

  // Websocket: личный канал пользователя для toast и live-обновлений диалогов
  useEffect(() => {
    if (!user) {
      if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
      return;
    }

    const backend = process.env.REACT_APP_BACKEND_URL || '';
    const wsBase = backend.replace(/^http/, 'ws');
    const url = `${wsBase}/api/ws/user`;

    let stopped = false;

    const connect = () => {
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;
        ws.onopen = () => {
          // ping каждые 25 секунд
          if (pingRef.current) clearInterval(pingRef.current);
          pingRef.current = setInterval(() => {
            try { ws.readyState === 1 && ws.send('ping'); } catch {}
          }, 25000);
        };
        ws.onmessage = (ev) => {
          let data;
          try { data = JSON.parse(ev.data); } catch { return; }
          if (data.type === 'pong') return;
          // Тендер удалён супер-админом (или владельцем) — убираем из всех списков
          // во всех открытых дашбордах и закрываем модалки.
          if (data.type === 'tender:removed') {
            window.dispatchEvent(new CustomEvent('tender:removed', {
              detail: {
                tender_id: data.tender_id,
                lot_name: data.lot_name || '',
                deleted_by: data.deleted_by || '',
              },
            }));
            const label = data.lot_name ? `«${data.lot_name}»` : '';
            toast.info(`Тендер ${label} удалён администратором`.trim());
            return;
          }
          if (data.type === 'chat:new_message' || data.type === 'chat:message_sent') {
            // 1) Оптимистично обновляем список диалогов локально (для бейджей).
            setThreads(prev => {
              const next = [...prev];
              const idx = next.findIndex(t =>
                t.tender_id === data.tender_id &&
                t.supplier_email === (data.supplier_email || '').toLowerCase()
              );
              const msg = data.message || {};
              if (idx >= 0) {
                const t = { ...next[idx] };
                t.last_message = {
                  text: msg.message_text || '',
                  sender_email: msg.sender_email,
                  sender_role: msg.sender_role,
                  timestamp: msg.timestamp,
                  has_attachments: !!(msg.attachments && msg.attachments.length),
                };
                t.total_messages = (t.total_messages || 0) + 1;
                t.updated_at = msg.timestamp || t.updated_at;
                if (data.type === 'chat:new_message') {
                  t.unread_count = (t.unread_count || 0) + 1;
                }
                next[idx] = t;
                next.sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''));
              }
              return next;
            });
            if (data.type === 'chat:new_message') {
              setUnread(u => u + 1);
            }
            // 2) Эмитим event подписчикам (виджет добавит в открытый диалог).
            emit(data);
            // 3) Если в локальном кэше треда нет — делаем refetch (новый диалог).
            setThreads(curr => {
              const exists = curr.some(t =>
                t.tender_id === data.tender_id &&
                t.supplier_email === (data.supplier_email || '').toLowerCase()
              );
              if (!exists) refreshThreads();
              return curr;
            });
            // Toast получателю
            if (data.type === 'chat:new_message') {
              const msg = data.message || {};
              const lotLabel = data.tender_lot_number
                ? `${t('chat.lot_label')}${data.tender_lot_number}`
                : (data.tender_lot_name || t('chat.tender_default'));
              const previewText = msg.message_text || (msg.attachments?.length ? `📎 ${t('chat.attachment')}` : '');
              toast.message(`${data.sender_company} | ${lotLabel}`, {
                description: truncate(previewText, 50),
                duration: 6000,
                action: {
                  label: t('chat.action_open'),
                  onClick: () => openThread(data.tender_id, data.supplier_email),
                },
              });
            }
          }
        };
        ws.onclose = () => {
          if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
          wsRef.current = null;
          if (!stopped) {
            reconnectRef.current = setTimeout(connect, 4000);
          }
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch {
        if (!stopped) reconnectRef.current = setTimeout(connect, 4000);
      }
    };

    connect();
    refreshThreads();

    return () => {
      stopped = true;
      if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
      if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
      if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
    };
  }, [user, refreshThreads, openThread]);

  return (
    <ChatContext.Provider value={{
      open, setOpen, activeKey, setActiveKey,
      threads, unread, refreshThreads,
      openThread, closeWidget, toggleWidget,
      subscribe,
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
};
